import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API, setToken } from "@/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

export default function Login() {
  const [identities, setIdentities] = useState([]);
  const [roles, setRoles] = useState(null);
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${API}/auth/dev-identities`).then((r) => (r.ok ? r.json() : []))
      .then(setIdentities).catch(() => setIdentities([]));
    fetch(`${API}/auth/role-accounts`).then((r) => (r.ok ? r.json() : null))
      .then(setRoles).catch(() => setRoles(null));
  }, []);

  const signInWithPassword = async (event) => {
    event.preventDefault();
    setBusy("password");
    setError(null);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = body.detail;
        throw new Error(typeof detail === "string" ? detail
          : Array.isArray(detail) ? detail.map((d) => d.msg || d).join("; ")
            : "Sign-in failed");
      }
      setToken(body.access_token);
      navigate("/");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const devSignIn = async (email) => {
    setBusy(email);
    setError(null);
    try {
      const res = await fetch(`${API}/auth/dev-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Sign-in failed");
      const data = await res.json();
      setToken(data.access_token);
      navigate("/");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6 lg:p-10">
      <div className="mx-auto grid max-w-5xl gap-6 lg:grid-cols-[22rem_1fr]">
        <Card className="h-fit" data-testid="login-card">
          <CardHeader>
            <div className="mb-2 flex items-center gap-3">
              <span className="grid h-10 w-10 place-items-center rounded-md
                               bg-[var(--hcd-blue)] text-[10px] font-bold text-white"
                    aria-hidden="true">HCD</span>
              <span className="text-xs font-semibold uppercase tracking-widest text-slate-600">
                Health Care District
              </span>
            </div>
            <CardTitle className="text-2xl">Enterprise Service Center</CardTitle>
            <CardDescription>
              Sign in with your email address and password.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <form onSubmit={signInWithPassword} className="space-y-3">
              <div className="space-y-1">
                <label htmlFor="login-email" className="text-sm font-medium">Email address</label>
                <Input id="login-email" type="email" data-testid="login-email"
                       autoComplete="username" value={form.email}
                       onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="space-y-1">
                <label htmlFor="login-password" className="text-sm font-medium">Password</label>
                <Input id="login-password" type="password" data-testid="login-password"
                       autoComplete="current-password" value={form.password}
                       onChange={(e) => setForm({ ...form, password: e.target.value })} />
              </div>
              <Button type="submit" data-testid="login-submit"
                      className="w-full bg-[var(--hcd-blue)] hover:bg-[var(--hcd-blue-dark)]"
                      disabled={busy !== null}>
                {busy === "password" ? "Signing in…" : "Sign in"}
              </Button>
            </form>
            {error && (
              <p data-testid="login-error" className="text-sm text-red-900">{error}</p>
            )}
            <div className="border-t pt-3">
              <Button variant="ghost" size="sm" data-testid="login-show-picker"
                      onClick={() => setShowPicker(!showPicker)}>
                {showPicker ? "Hide the development picker" : "Development sign-in (no password)"}
              </Button>
              {showPicker && (
                <div className="mt-2 space-y-2" data-testid="login-picker">
                  <p className="text-xs text-slate-600">
                    Non-production only. Microsoft Entra replaces both of these in production.
                  </p>
                  {identities.map((i) => (
                    <Button key={i.email} data-testid={`dev-login-${i.email}`} variant="outline"
                            className="w-full justify-start text-left" disabled={busy !== null}
                            onClick={() => devSignIn(i.email)}>
                      {busy === i.email ? "Signing in…" : `${i.name} — ${i.email}`}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card data-testid="login-roles">
          <CardHeader>
            <CardTitle className="text-lg">Who can I sign in as?</CardTitle>
            <CardDescription>
              {roles?.definition
               || "One account per role, so you can see exactly what that role sees."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="divide-y divide-slate-200">
              {(roles?.rows || []).map((row) => (
                <li key={row.email} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 py-2"
                    data-testid={`login-role-${row.email}`}>
                  <span className="font-medium text-slate-900">{row.title}</span>
                  <button type="button"
                          className="text-sm text-[var(--hcd-blue-link)] underline"
                          data-testid={`login-use-${row.email}`}
                          onClick={() => setForm({ ...form, email: row.email })}>
                    {row.email}
                  </button>
                  <span className="basis-full text-xs text-slate-600">
                    {row.name} — {row.sees}
                  </span>
                </li>
              ))}
              {roles && roles.rows.length === 0 && (
                <li className="py-2 text-sm text-slate-600" data-testid="login-roles-empty">
                  The role accounts have not been seeded in this environment yet.
                </li>
              )}
            </ul>
            {roles && (
              <p className="mt-3 text-xs text-slate-600" data-testid="login-roles-method">
                {roles.method}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
